/**
 * 全屏API封装工具
 * 支持各种浏览器的全屏功能
 */

const methodMap = [
  ["requestFullscreen", "exitFullscreen", "fullscreenElement", "fullscreenEnabled", "fullscreenchange", "fullscreenerror"],
  [
    "webkitRequestFullscreen",
    "webkitExitFullscreen",
    "webkitFullscreenElement",
    "webkitFullscreenEnabled",
    "webkitfullscreenchange",
    "webkitfullscreenerror"
  ],
  [
    "webkitRequestFullScreen",
    "webkitCancelFullScreen",
    "webkitCurrentFullScreenElement",
    "webkitCancelFullScreen",
    "webkitfullscreenchange",
    "webkitfullscreenerror"
  ],
  [
    "mozRequestFullScreen",
    "mozCancelFullScreen",
    "mozFullScreenElement",
    "mozFullScreenEnabled",
    "mozfullscreenchange",
    "mozfullscreenerror"
  ],
  ["msRequestFullscreen", "msExitFullscreen", "msFullscreenElement", "msFullscreenEnabled", "MSFullscreenChange", "MSFullscreenError"]
];

const nativeAPI = (() => {
  if (typeof document === "undefined") {
    return false;
  }

  const unprefixedMethods = methodMap[0];
  const returnValue = {};

  for (const methodList of methodMap) {
    const exitFullscreenMethod = methodList?.[1];
    if (exitFullscreenMethod in document) {
      for (const [index, method] of methodList.entries()) {
        returnValue[unprefixedMethods[index]] = method;
      }
      return returnValue;
    }
  }
  return false;
})();

const eventNameMap = {
  change: nativeAPI ? nativeAPI.fullscreenchange : "",
  error: nativeAPI ? nativeAPI.fullscreenerror : ""
};

let timer;

const startTimer = (reject) => {
  if (timer) {
    clearTimeout(timer);
  }
  timer = setTimeout(() => {
    clearTimeout(timer);
    reject(new Error("Promise cancelled"));
  }, 10000);
};

let screenfull = {
  request(element = document.documentElement, options) {
    return new Promise((resolve, reject) => {
      if (!nativeAPI) {
        reject(new Error("Fullscreen API is not supported"));
        return;
      }
      startTimer(reject);
      const onFullScreenEntered = () => {
        screenfull.off("change", onFullScreenEntered);
        if (timer) {
          clearTimeout(timer);
        }
        resolve();
      };

      screenfull.on("change", onFullScreenEntered);

      const returnPromise = element[nativeAPI.requestFullscreen](options);

      if (returnPromise instanceof Promise) {
        returnPromise
          .then(onFullScreenEntered)
          .catch((err) => {
            // 这里可以替换为你们自己的埋点或日志
            // console.error("screenfull:request", err && err.message);
            reject(err);
          })
          .finally(() => {
            if (timer) {
              clearTimeout(timer);
            }
          });
      } else {
        if (timer) {
          clearTimeout(timer);
        }
      }
    }).catch(() => {});
  },
  exit() {
    return new Promise((resolve, reject) => {
      if (!nativeAPI) {
        reject(new Error("Fullscreen API is not supported"));
        return;
      }
      startTimer(reject);
      if (!screenfull.isFullscreen) {
        if (timer) {
          clearTimeout(timer);
        }
        resolve();
        return;
      }

      const onFullScreenExit = () => {
        screenfull.off("change", onFullScreenExit);
        if (timer) {
          clearTimeout(timer);
        }
        resolve();
      };

      screenfull.on("change", onFullScreenExit);

      const returnPromise = document[nativeAPI.exitFullscreen]();

      if (returnPromise instanceof Promise) {
        returnPromise
          .then(onFullScreenExit)
          .catch((err) => {
            // 这里可以替换为你们自己的埋点或日志
            // console.error("screenfull:exit", err && err.message);
            reject(err);
          })
          .finally(() => {
            if (timer) {
              clearTimeout(timer);
            }
          });
      } else {
        if (timer) {
          clearTimeout(timer);
        }
      }
    }).catch(() => {});
  },
  toggle(element, options) {
    return screenfull.isFullscreen ? screenfull.exit() : screenfull.request(element, options);
  },
  onchange(callback) {
    screenfull.on("change", callback);
  },
  onerror(callback) {
    screenfull.on("error", callback);
  },
  on(event, callback) {
    const eventName = eventNameMap[event];
    if (eventName) {
      document.addEventListener(eventName, callback, false);
    }
  },
  off(event, callback) {
    const eventName = eventNameMap[event];
    if (eventName) {
      document.removeEventListener(eventName, callback, false);
    }
  },
  get isFullscreen() {
    return !!(nativeAPI && document[nativeAPI.fullscreenElement]);
  },
  get element() {
    return nativeAPI ? document[nativeAPI.fullscreenElement] ?? undefined : undefined;
  },
  get isEnabled() {
    return !!(nativeAPI && document[nativeAPI.fullscreenEnabled]);
  },
  raw: nativeAPI || undefined
};

if (!nativeAPI) {
  screenfull = {
    ...screenfull,
    isEnabled: false,
    request: async () => {},
    exit: async () => {},
    toggle: async () => {},
    onchange: () => {},
    onerror: () => {},
    on: () => {},
    off: () => {},
    get isFullscreen() {
      return false;
    },
    get element() {
      return undefined;
    }
  };
}

export default screenfull;